/**
 * DATA SOURCE FACTORY
 *
 * Returns the appropriate DataSource based on workspace type.
 * Cloud workspaces use the API; local workspaces use SQLite (Tauri only).
 *
 * LocalDataSource is loaded via dynamic import() so it's never bundled in web builds.
 */

import type { DataSource } from "./types";
import { isTauri } from "@/hooks/usePlatform";

// Singleton instances
let cloudDataSource: DataSource | null = null;
let localDataSource: DataSource | null = null;

/**
 * Get the DataSource for a given workspace type.
 *
 * - "cloud" → CloudDataSource (always available)
 * - "local" → LocalDataSource (Tauri only, dynamic import)
 *
 * Throws if local is requested outside Tauri runtime.
 */
export async function getDataSource(type: "cloud" | "local"): Promise<DataSource> {
  if (type === "cloud") {
    if (!cloudDataSource) {
      const { CloudDataSource } = await import("./cloud-source");
      cloudDataSource = new CloudDataSource();
    }
    return cloudDataSource;
  }

  // Local workspace — Tauri only
  if (!isTauri()) {
    throw new Error("Local workspaces require the desktop app");
  }

  if (!localDataSource) {
    const { LocalDataSource } = await import("./local-source");
    localDataSource = new LocalDataSource();
  }
  return localDataSource;
}

/**
 * Convenience: get DataSource for the currently active workspace.
 * Used by stores that already know the workspace type.
 */
export function getDataSourceSync(type: "cloud" | "local"): DataSource | null {
  if (type === "cloud") return cloudDataSource;
  if (type === "local") return localDataSource;
  return null;
}
